import React, { useState, useEffect } from 'react';
import { API } from '../../../api/URL_API';
import { Button, Container, Row } from 'react-bootstrap';
import { ArrowRightCircleFill, ArrowRight } from 'react-bootstrap-icons';
import { Card, CardActionArea, CardContent, CardMedia } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import Skeleton from '@mui/material/Skeleton';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import '../styles/PaginationDetalles.css';
import { useSucursal } from '../../../contexts/SucursalContext';
import { createSlug } from '../../../utils/slug';

/**
 * Obtiene N categorías aleatorias de forma reproducible
 * @param {Array} allCategories - Lista de todas las categorías
 * @param {number} count - Cantidad de categorías a seleccionar (default: 4)
 * @returns {Array} Categorías seleccionadas en orden original
 */
const getRandomCategories = (allCategories, count = 4) => {
    if (!allCategories || allCategories.length === 0) return [];
    
    const indices = [];
    const selectedIndices = new Set();
    
    // Seleccionar índices únicos aleatorios
    while (indices.length < Math.min(count, allCategories.length)) {
        const randomIndex = Math.floor(Math.random() * allCategories.length);
        if (!selectedIndices.has(randomIndex)) {
            selectedIndices.add(randomIndex);
            indices.push(randomIndex);
        }
    }
    
    // Retornar categorías en el orden original de la lista
    return indices
        .sort((a, b) => a - b)
        .map(idx => allCategories[idx]);
};

function CategoriasHome() {
    const [categorias, setCategorias] = useState([]);
    const [allCategorias, setAllCategorias] = useState([]);
    const { idSucursal } = useSucursal();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);

    // Cargar todas las categorías disponibles
    useEffect(() => {
        setIsLoading(true);
        fetch(`${API}/api/mostrarCategorias/categorias`)
            .then(response => response.json())
            .then(data => {
                const todasLasCategorias = data.flatMap(seccion =>
                    seccion.categorias ? seccion.categorias : []
                );
                setAllCategorias(todasLasCategorias);
                
                // Seleccionar categorías aleatorias una sola vez al cargar
                if (todasLasCategorias.length > 0) {
                    setCategorias(getRandomCategories(todasLasCategorias, 4));
                }
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error al cargar las categorias:', error);
                setIsLoading(false);
            });
    }, []);

    // Cambiar categorías cada 24 horas
    useEffect(() => {
        if (allCategorias.length === 0) return;

        const interval = setInterval(() => {
            setCategorias(getRandomCategories(allCategorias, 4));
        }, 86400000); // 24 horas

        return () => clearInterval(interval);
    }, [allCategorias]);

    const handleMoreCollection = () => {
        if (idSucursal) {
            navigate(`/${idSucursal}/Catalogo`);
        } else {
            console.error('idSucursal no disponible');
        }
    };

    return (
        <Container fluid className='p-3 p-md-5'>
            <Row className='mb-0'>
                <div className='d-flex align-items-center justify-content-between w-100'>
                    <h4 className='ms-2 color-primary fw-bold fs-5 fs-md-4'>
                        Nuestras categorías
                    </h4>
                    <Button
                        variant='link'
                        className='btn-more-collection text-decoration-none'
                        onClick={handleMoreCollection}
                    >
                        Ver todas
                        <ArrowRightCircleFill size={18} className='ms-2' />
                    </Button>
                </div>
            </Row>

            <Swiper
                slidesPerView={4}
                spaceBetween={20}
                loop={true}
                pagination={{
                    clickable: true,
                    dynamicBullets: true
                }}
                autoplay={{ delay: 5000 }}
                modules={[Pagination, Autoplay]}
                breakpoints={{
                    0: { slidesPerView: 1.2 },
                    480: { slidesPerView: 1.5 },
                    768: { slidesPerView: 3 },
                    1024: { slidesPerView: 4 }
                }}
                className="mySwiper"
            >
                {isLoading
                    ? Array.from({ length: 4 }).map((_, idx) => (
                        <SwiperSlide key={idx} className="d-flex my-3">
                            <Card
                                className='product-card mt-2'
                                style={{
                                    boxShadow: '0 2px 8px rgba(43, 35, 94, 0.15)',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    backgroundColor: '#f5efe8',
                                    width: '100%'
                                }}
                            >
                                <CardActionArea>
                                    <Skeleton variant="rectangular" height={300} width="100%" animation="wave" />
                                    <CardContent className='text-center text1-cr'>
                                        <Skeleton variant="text" width="80%" height={30} />
                                        <Skeleton variant="text" width="60%" height={25} />
                                        <Skeleton variant="text" width="50%" height={20} />
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </SwiperSlide>
                    ))
                    : categorias.map((categoria) => (
                        <SwiperSlide key={categoria.idCategoria} className="d-flex my-3">
                            <Card
                                className="product-card mt-2"
                                style={{
                                    boxShadow: '0 2px 8px rgba(43, 35, 94, 0.15)',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    cursor: 'pointer',
                                    backgroundColor: '#f5efe8',
                                    width: '100%'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(43, 35, 94, 0.25)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(43, 35, 94, 0.15)';
                                }}
                            >
                                <Link
                                    to={`/${idSucursal}/Productos/${createSlug(categoria.nomCategoria)}`}
                                    className="text-decoration-none d-flex flex-column h-100"
                                >
                                    <CardMedia
                                        component='img'
                                        image={`${categoria.imgCategoria}`}
                                        alt={`ver ${categoria.nomCategoria}`}
                                        title={`crisha-${categoria.nomCategoria}`}
                                        className='card-media-image'
                                        style={{
                                            aspectRatio: '1/1',
                                            objectFit: 'cover',
                                            height: '100%'
                                        }}
                                    />
                                    <CardContent className='p-3 d-flex flex-column justify-content-between flex-grow-1'>
                                        <div className='d-flex justify-content-between align-items-flex-start gap-2'>
                                            <div>
                                                <h5 className='fw-bold mb-2 color-primary' style={{ fontSize: '1rem', lineHeight: '1.3' }}>
                                                    {categoria.nomCategoria}
                                                </h5>
                                                <p className='mb-0 text-muted' style={{ fontSize: '0.85rem' }}>
                                                    {categoria.numProductos || '0'} productos
                                                </p>
                                            </div>
                                            <ArrowRight size={20} className='color-primary flex-shrink-0' />
                                        </div>
                                    </CardContent>
                                </Link>
                            </Card>
                        </SwiperSlide>
                    ))}
            </Swiper>
        </Container>
    );
}

export default CategoriasHome;
